/**
 * Frontend-only demo simulation for caterer invoicing.
 *
 * Purpose: let the upload -> extract -> submit -> reconcile flow run end to
 * end with NO backend calls, for live demos / UAT walkthroughs. Six sample
 * PDFs (see /demo-invoices in the repo root) are matched by filename; when a
 * match is found, create-caterer-invoice.jsx, caterer-invoice-review-modal.jsx
 * and caterer-invoice.jsx short-circuit their normal API calls and use the
 * canned data below instead.
 *
 * This whole module is a single, deletable seam: remove the import + the
 * `IS_DEMO_MODE`/`findDemoScenario(...)` branches in those three files and
 * the app is back to calling the real backend exclusively.
 */

// Single switch for the whole demo. true = every caterer-invoicing screen
// runs entirely off local data, no network calls at all (no toggle exposed
// in the UI — flip this constant, or delete this module + its call sites,
// to fully restore the real backend).
export const IS_DEMO_MODE = true;

// DEMO ONLY: the whole demo (all 7 sample invoices, including the 6 that are
// really Indian entities/airports) displays amounts in this currency instead
// of the real per-tenant base currency (see culture.js / getCultureFromLs()
// on the real app). Post-demo, real multi-currency support needs the actual
// architecture described in-conversation (a currencyCode field per invoice +
// the IBR exchange-rate pattern already used by crew deposits/bank
// transfers) — this constant is just a display-layer stand-in for the demo.
export const DEMO_CURRENCY_CODE = 'MAD';

export function formatDemoCurrency(amount) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: DEMO_CURRENCY_CODE }).format(amount || 0);
}

const nowIso = () => new Date().toISOString();

const baseVendor = (vendorId, vendorCode, vendorName, stateCode) => ({
  matchStatus: 'matched',
  vendorId,
  vendorCode,
  vendorName,
  stateCode
});

const SCENARIOS = [
  {
    fileName: 'cafs-service-matched.pdf',
    invoiceType: 'Service',
    preview: {
      extractedData: {
        invoiceNumber: 'HYDI/00199/26-27',
        invoiceDate: '2026-08-16',
        catererName: 'Casino Air Caterers & Flight Services (CAFS)',
        gstIn: '36AACCA2654H1ZV',
        locationCode: 'HYD',
        sector: 'HYD-DEL',
        startDate: '2026-08-01',
        endDate: '2026-08-15',
        subTotal: 14691.00,
        taxAmount: 2644.38,
        taxLabel: 'TVA @ 18%',
        total: 17335.38,
        extractionMethod: 'text-layer'
      },
      resolvedVendor: baseVendor('v-cafs-hyd', 'CAFS', 'Casino Air Caterers & Flight Services (CAFS)', 'TS'),
      missingFields: []
    },
    finalInvoice: {
      invoiceId: 'HYDI/00199/26-27',
      invoiceType: 'Service',
      locationCode: 'HYD',
      catererName: 'Casino Air Caterers & Flight Services (CAFS)',
      gstIn: '36AACCA2654H1ZV',
      address: "Rajiv Gandhi Int'l Airport, Shamshabad, Hyderabad - 500108",
      period: '01-Aug-2026 to 15-Aug-2026',
      startDate: '2026-08-01',
      endDate: '2026-08-15',
      date: nowIso(),
      flightNumber: 'QP 1405',
      sector: 'HYD-DEL',
      subTotal: 14691.00,
      taxAmount: 2644.38,
      taxLabel: 'TVA @ 18%',
      total: 17335.38,
      variance: 0,
      age: 0,
      status: 'Under Review',
      paymentTerms: 'Net30',
      vendorMatchStatus: 'matched',
      lineItems: [
        { itemCode: 'SRV-BEV-CHILL', description: 'Beverage Chilling Charges', billedQty: 15, dbQty: 15, dbSource: 'uplifts (Confirmed Catering Records)', unitRate: 40.00, billedAmount: 600.00, dbAmount: 600.00, variance: 0, status: 'Matched', varianceReason: 'Matches 15 confirmed catering upliftment records for HYD, 01-15 Aug 2026.' },
        { itemCode: 'SRV-HANDLING', description: 'Handling Charges', billedQty: 15, dbQty: 15, dbSource: 'flightSchedules (Real Flight Ops Records)', unitRate: 290.00, billedAmount: 4350.00, dbAmount: 4350.00, variance: 0, status: 'Matched', varianceReason: 'Matches 15 completed HYD departures recorded in flightSchedules for this period.' },
        { itemCode: 'SRV-HILIFT', description: 'Hi-Lift Charges', billedQty: 30, dbQty: 30, dbSource: 'uplifts x2 (Arrival + Departure Positioning)', unitRate: 250.00, billedAmount: 7500.00, dbAmount: 7500.00, variance: 0, status: 'Matched', varianceReason: 'Hi-Lift is billed twice per flight (arrival + departure); 15 flights x 2 = 30 confirmed.' },
        { itemCode: 'SRV-LEVY', description: 'Airport Levy @ 18%', billedQty: 1, dbQty: 1, dbSource: 'Computed — 18% of other line items', unitRate: 2241.00, billedAmount: 2241.00, dbAmount: 2241.00, variance: 0, status: 'Matched', varianceReason: '18% of MAD 12,450.00 (sum of other billed lines) = MAD 2,241.00 — arithmetic verified.' }
      ]
    }
  },
  {
    fileName: 'cafs-service-variance.pdf',
    invoiceType: 'Service',
    preview: {
      extractedData: {
        invoiceNumber: 'HYDI/00214/26-27',
        invoiceDate: '2026-09-01',
        catererName: 'Casino Air Caterers & Flight Services (CAFS)',
        gstIn: '36AACCA2654H1ZV',
        locationCode: 'HYD',
        sector: 'HYD-DEL',
        startDate: '2026-08-16',
        endDate: '2026-08-31',
        subTotal: 15670.40,
        taxAmount: 2820.67,
        taxLabel: 'TVA @ 18%',
        total: 18491.07,
        extractionMethod: 'text-layer'
      },
      resolvedVendor: baseVendor('v-cafs-hyd', 'CAFS', 'Casino Air Caterers & Flight Services (CAFS)', 'TS'),
      missingFields: []
    },
    finalInvoice: {
      invoiceId: 'HYDI/00214/26-27',
      invoiceType: 'Service',
      locationCode: 'HYD',
      catererName: 'Casino Air Caterers & Flight Services (CAFS)',
      gstIn: '36AACCA2654H1ZV',
      address: "Rajiv Gandhi Int'l Airport, Shamshabad, Hyderabad - 500108",
      period: '16-Aug-2026 to 31-Aug-2026',
      startDate: '2026-08-16',
      endDate: '2026-08-31',
      date: nowIso(),
      flightNumber: 'QP 1405',
      sector: 'HYD-DEL',
      subTotal: 15670.40,
      taxAmount: 2820.67,
      taxLabel: 'TVA @ 18%',
      total: 18491.07,
      variance: 580.00,
      age: 0,
      status: 'Under Review',
      paymentTerms: 'Net30',
      vendorMatchStatus: 'matched',
      lineItems: [
        { itemCode: 'SRV-BEV-CHILL', description: 'Beverage Chilling Charges', billedQty: 16, dbQty: 16, dbSource: 'uplifts (Confirmed Catering Records)', unitRate: 40.00, billedAmount: 640.00, dbAmount: 640.00, variance: 0, status: 'Matched', varianceReason: 'Matches 16 confirmed catering upliftment records for HYD, 16-31 Aug 2026.' },
        { itemCode: 'SRV-HANDLING', description: 'Handling Charges', billedQty: 16, dbQty: 14, dbSource: 'flightSchedules (Real Flight Ops Records)', unitRate: 290.00, billedAmount: 4640.00, dbAmount: 4060.00, variance: 580.00, status: 'Variance Flagged', varianceReason: 'Billed for 16 handling turnarounds, but flightSchedules shows only 14 completed HYD departures for QP 1405 in this period — 2 legs are flagged flightStatus: cancelled.' },
        { itemCode: 'SRV-HILIFT', description: 'Hi-Lift Charges', billedQty: 32, dbQty: 32, dbSource: 'uplifts x2 (Arrival + Departure Positioning)', unitRate: 250.00, billedAmount: 8000.00, dbAmount: 8000.00, variance: 0, status: 'Matched', varianceReason: 'Hi-Lift basis uses confirmed uplift count (16 x 2 = 32), independent of the flight-ops discrepancy above.' },
        { itemCode: 'SRV-LEVY', description: 'Airport Levy @ 18%', billedQty: 1, dbQty: 1, dbSource: 'Computed — 18% of other line items', unitRate: 2390.40, billedAmount: 2390.40, dbAmount: 2390.40, variance: 0, status: 'Matched', varianceReason: '18% of MAD 13,280.00 (sum of other billed lines) = MAD 2,390.40 — arithmetic is correct even though the base includes the disputed handling charge.' }
      ]
    }
  },
  {
    fileName: 'tajsats-uplifts-variance.pdf',
    invoiceType: 'Uplifts',
    preview: {
      extractedData: {
        invoiceNumber: 'TS-BOM/AUG-I/9482',
        invoiceDate: '2026-08-16',
        catererName: 'TajSATS Air Catering Ltd',
        gstIn: '27AAACT2948J1ZG',
        locationCode: 'BOM',
        sector: 'BOM-DEL',
        startDate: '2026-08-01',
        endDate: '2026-08-15',
        subTotal: 33920.00,
        taxAmount: 1696.00,
        taxLabel: 'TVA @ 5%',
        total: 35616.00,
        extractionMethod: 'text-layer'
      },
      resolvedVendor: baseVendor('v-tajsats-bom', 'TAJSATS', 'TajSATS Air Catering Ltd', 'MH'),
      missingFields: []
    },
    finalInvoice: {
      invoiceId: 'TS-BOM/AUG-I/9482',
      invoiceType: 'Uplifts',
      locationCode: 'BOM',
      catererName: 'TajSATS Air Catering Ltd',
      gstIn: '27AAACT2948J1ZG',
      address: "Chhatrapati Shivaji Maharaj Int'l Airport, Sahar, Mumbai - 400099",
      period: '01-Aug-2026 to 15-Aug-2026',
      startDate: '2026-08-01',
      endDate: '2026-08-15',
      date: nowIso(),
      flightNumber: 'QP 1102 / QP 1104 / QP 1308',
      sector: 'BOM-DEL',
      subTotal: 33920.00,
      taxAmount: 1696.00,
      taxLabel: 'TVA @ 5%',
      total: 35616.00,
      variance: 570.00,
      age: 0,
      status: 'Under Review',
      paymentTerms: 'Net30',
      vendorMatchStatus: 'matched',
      lineItems: [
        { itemCode: 'ML-BOB-SAND', description: 'Buy On Board (BOB) Sandwiches & Wraps', billedQty: 420, dbQty: 420, dbSource: 'uplifts.items (Catering Officer sign-off)', unitRate: 15.00, billedAmount: 6300.00, dbAmount: 6300.00, variance: 0, status: 'Matched' },
        { itemCode: 'ML-CORP-HOT', description: 'Corporate Premium Hot Meals', billedQty: 310, dbQty: 295, dbSource: 'uplifts.items (Catering Officer sign-off)', unitRate: 38.00, billedAmount: 11780.00, dbAmount: 11210.00, variance: 570.00, status: 'Variance Flagged', varianceReason: 'Billed 310 meals, but the signed Catering Officer physical uplift record confirms only 295 meals.' },
        { itemCode: 'ML-WEB-PRE', description: 'Web Pre-Booked Vegetarian / Non-Veg Meals', billedQty: 580, dbQty: 580, dbSource: 'uplifts.items (Catering Officer sign-off)', unitRate: 24.00, billedAmount: 13920.00, dbAmount: 13920.00, variance: 0, status: 'Matched' },
        { itemCode: 'ML-CREW-STD', description: 'Cockpit & Cabin Crew Standard Meals', billedQty: 160, dbQty: 160, dbSource: 'uplifts.items (Catering Officer sign-off)', unitRate: 12.00, billedAmount: 1920.00, dbAmount: 1920.00, variance: 0, status: 'Matched' }
      ]
    }
  },
  {
    fileName: 'cafs-uplifts-matched.pdf',
    invoiceType: 'Uplifts',
    preview: {
      extractedData: {
        invoiceNumber: 'CAFS/HYD/UPL/26/044',
        invoiceDate: '2026-09-01',
        catererName: 'Casino Air Caterers & Flight Services (CAFS)',
        gstIn: '36AACCA2654H1ZV',
        locationCode: 'HYD',
        sector: 'HYD-BLR',
        startDate: '2026-08-16',
        endDate: '2026-08-31',
        subTotal: 19000.00,
        taxAmount: 950.00,
        taxLabel: 'TVA @ 5%',
        total: 19950.00,
        extractionMethod: 'text-layer'
      },
      resolvedVendor: baseVendor('v-cafs-hyd', 'CAFS', 'Casino Air Caterers & Flight Services (CAFS)', 'TS'),
      missingFields: []
    },
    finalInvoice: {
      invoiceId: 'CAFS/HYD/UPL/26/044',
      invoiceType: 'Uplifts',
      locationCode: 'HYD',
      catererName: 'Casino Air Caterers & Flight Services (CAFS)',
      gstIn: '36AACCA2654H1ZV',
      address: "Rajiv Gandhi Int'l Airport, Shamshabad, Hyderabad - 500108",
      period: '16-Aug-2026 to 31-Aug-2026',
      startDate: '2026-08-16',
      endDate: '2026-08-31',
      date: nowIso(),
      flightNumber: 'QP 1405 / QP 1406',
      sector: 'HYD-BLR',
      subTotal: 19000.00,
      taxAmount: 950.00,
      taxLabel: 'TVA @ 5%',
      total: 19950.00,
      variance: 0,
      age: 0,
      status: 'Under Review',
      paymentTerms: 'Net30',
      vendorMatchStatus: 'matched',
      lineItems: [
        { itemCode: 'ML-BOB-SNACK', description: 'Pre-packaged Dry Snacks & Chips', billedQty: 600, dbQty: 600, dbSource: 'uplifts.items (Catering Officer sign-off)', unitRate: 9.00, billedAmount: 5400.00, dbAmount: 5400.00, variance: 0, status: 'Matched' },
        { itemCode: 'ML-WEB-MEAL', description: 'Pre-booked Hot Meals & Biryani', billedQty: 400, dbQty: 400, dbSource: 'uplifts.items (Catering Officer sign-off)', unitRate: 34.00, billedAmount: 13600.00, dbAmount: 13600.00, variance: 0, status: 'Matched' }
      ]
    }
  },
  {
    fileName: 'oberoi-bulkdelivery-matched.pdf',
    invoiceType: 'Bulk Deliveries',
    preview: {
      extractedData: {
        invoiceNumber: 'OFS/DEL/2026/0411',
        invoiceDate: '2026-08-11',
        catererName: 'Oberoi Flight Services',
        gstIn: '07AAACO8812K1Z8',
        locationCode: 'DEL',
        poNumber: 'PO-2026-0891',
        startDate: '2026-08-10',
        endDate: '2026-08-10',
        subTotal: 24000.00,
        taxAmount: 4320.00,
        taxLabel: 'TVA @ 18%',
        total: 28320.00,
        extractionMethod: 'text-layer'
      },
      resolvedVendor: baseVendor('v-ofs-del', 'OFS', 'Oberoi Flight Services', 'DL'),
      missingFields: []
    },
    finalInvoice: {
      invoiceId: 'OFS/DEL/2026/0411',
      invoiceType: 'Bulk Deliveries',
      locationCode: 'DEL',
      catererName: 'Oberoi Flight Services',
      gstIn: '07AAACO8812K1Z8',
      address: "Indira Gandhi Int'l Airport, Terminal 3 Cargo Complex, New Delhi - 110037",
      poNumber: 'PO-2026-0891',
      period: 'Delivery: 10-Aug-2026',
      startDate: '2026-08-10',
      endDate: '2026-08-10',
      date: nowIso(),
      sector: 'DEL',
      subTotal: 24000.00,
      taxAmount: 4320.00,
      taxLabel: 'TVA @ 18%',
      total: 28320.00,
      variance: 0,
      age: 0,
      status: 'Under Review',
      paymentTerms: 'Net45',
      vendorMatchStatus: 'matched',
      lineItems: [
        { itemCode: 'BLK-WTR-250', description: 'Packaged Drinking Water 250ml (Cases of 24)', billedQty: 500, dbQty: 500, dbSource: 'purchaseOrderGrn (Dock Goods Receipt Note)', unitRate: 20.00, billedAmount: 10000.00, dbAmount: 10000.00, variance: 0, status: 'Matched' },
        { itemCode: 'BLK-JCE-200', description: 'Assorted Fruit Juices 200ml Tetra Pack (Cases of 30)', billedQty: 250, dbQty: 250, dbSource: 'purchaseOrderGrn (Dock Goods Receipt Note)', unitRate: 38.00, billedAmount: 9500.00, dbAmount: 9500.00, variance: 0, status: 'Matched' },
        { itemCode: 'BLK-CUTLERY', description: 'Biodegradable Meal Cutlery Kits', billedQty: 10000, dbQty: 10000, dbSource: 'purchaseOrderGrn (Dock Goods Receipt Note)', unitRate: 0.45, billedAmount: 4500.00, dbAmount: 4500.00, variance: 0, status: 'Matched' }
      ]
    }
  },
  {
    fileName: 'ambassadorskychef-bulkdelivery-variance.pdf',
    invoiceType: 'Bulk Deliveries',
    preview: {
      extractedData: {
        invoiceNumber: 'ASC/MAA/26/881',
        invoiceDate: '2026-08-19',
        catererName: 'Ambassador Sky Chef',
        gstIn: '33AAACA9922M1ZY',
        locationCode: 'MAA',
        poNumber: 'PO-2026-1104',
        startDate: '2026-08-18',
        endDate: '2026-08-18',
        subTotal: 9600.00,
        taxAmount: 1728.00,
        taxLabel: 'TVA @ 18%',
        total: 11328.00,
        extractionMethod: 'text-layer'
      },
      resolvedVendor: baseVendor('v-asc-maa', 'ASC', 'Ambassador Sky Chef', 'TN'),
      missingFields: []
    },
    finalInvoice: {
      invoiceId: 'ASC/MAA/26/881',
      invoiceType: 'Bulk Deliveries',
      locationCode: 'MAA',
      catererName: 'Ambassador Sky Chef',
      gstIn: '33AAACA9922M1ZY',
      address: 'Chennai Int\'l Airport, Meenambakkam, Chennai - 600027',
      poNumber: 'PO-2026-1104',
      period: 'Delivery: 18-Aug-2026',
      startDate: '2026-08-18',
      endDate: '2026-08-18',
      date: nowIso(),
      sector: 'MAA',
      subTotal: 9600.00,
      taxAmount: 1728.00,
      taxLabel: 'TVA @ 18%',
      total: 11328.00,
      variance: 1200.00,
      age: 0,
      status: 'Under Review',
      paymentTerms: 'Net30',
      vendorMatchStatus: 'matched',
      lineItems: [
        { itemCode: 'BLK-BOX-SNACK', description: 'Packaged Fresh Snack Boxes (Sandwich + Muffin)', billedQty: 800, dbQty: 700, dbSource: 'purchaseOrderGrn (Dock GRN Record)', unitRate: 12.00, billedAmount: 9600.00, dbAmount: 8400.00, variance: 1200.00, status: 'Variance Flagged', varianceReason: 'Caterer billed 800 snack boxes; the dock GRN signed receipt shows only 700 units received.' }
      ]
    }
  },
  {
    // DEMO ONLY: Morocco has no GST — this invoice deliberately carries no
    // gstIn/cgst/sgst/igst fields (Moroccan TVA instead, see taxLabel below).
    // The review modal's Financials tab branches on invoice.gstIn to decide
    // between a CGST/SGST/IGST breakdown and a generic tax line; a real
    // per-country tax-regime implementation is needed post-demo.
    fileName: 'atlasskyfood-uplifts-variance.pdf',
    invoiceType: 'Uplifts',
    preview: {
      extractedData: {
        invoiceNumber: 'ASF/CMN/2026/0091',
        invoiceDate: '2026-09-08',
        catererName: 'Atlas Skyfood Catering SARL',
        locationCode: 'CMN',
        sector: 'CMN-JFK',
        startDate: '2026-09-01',
        endDate: '2026-09-07',
        subTotal: 943670.00,
        taxAmount: 188734.00,
        total: 1132404.00,
        extractionMethod: 'text-layer'
      },
      resolvedVendor: baseVendor('v-atlas-cmn', 'ASF', 'Atlas Skyfood Catering SARL', null),
      missingFields: []
    },
    finalInvoice: {
      invoiceId: 'ASF/CMN/2026/0091',
      invoiceType: 'Uplifts',
      locationCode: 'CMN',
      catererName: 'Atlas Skyfood Catering SARL',
      address: "Zone Aéroportuaire Nord, Aéroport Mohammed V (CMN), Nouaceur, Casablanca-Settat, Morocco",
      taxRegistrationLabel: 'ICE / IF / RC',
      taxRegistrationValue: '000000000000000 / 00000000 / 000000',
      taxLabel: 'TVA @ 20%',
      period: '01-Sep-2026 to 07-Sep-2026',
      startDate: '2026-09-01',
      endDate: '2026-09-07',
      date: nowIso(),
      flightNumber: 'AT201',
      sector: 'CMN-JFK',
      subTotal: 943670.00,
      taxAmount: 188734.00,
      total: 1132404.00,
      variance: 15142.00,
      age: 0,
      status: 'Under Review',
      paymentTerms: 'Net30',
      vendorMatchStatus: 'matched',
      lineItems: [
        { itemCode: 'MAD-BREAKFAST', description: 'Breakfast Service (11 items)', billedQty: 5509, dbQty: 5250, dbSource: 'uplifts.items (Catering Officer sign-off)', unitRate: 18.00, billedAmount: 99162.00, dbAmount: 94500.00, variance: 4662.00, status: 'Variance Flagged', varianceReason: 'Billed 5,509 breakfast items across 7 flights; the signed Catering Officer uplift record confirms only 5,250 received on board — a shortfall of 259 units.' },
        { itemCode: 'MAD-TEA-BITES', description: 'Tea & Light Bites (25 items)', billedQty: 12390, dbQty: 12390, dbSource: 'uplifts.items (Catering Officer sign-off)', unitRate: 16.00, billedAmount: 198240.00, dbAmount: 198240.00, variance: 0, status: 'Matched' },
        { itemCode: 'MAD-PREM-ENTREE', description: 'Premium Entrées (14 items)', billedQty: 5698, dbQty: 5698, dbSource: 'uplifts.items (Catering Officer sign-off)', unitRate: 42.00, billedAmount: 239316.00, dbAmount: 239316.00, variance: 0, status: 'Matched' },
        { itemCode: 'MAD-STD-ENTREE', description: 'Standard Entrées (16 items)', billedQty: 6370, dbQty: 6370, dbSource: 'uplifts.items (Catering Officer sign-off)', unitRate: 26.00, billedAmount: 165620.00, dbAmount: 165620.00, variance: 0, status: 'Matched' },
        { itemCode: 'MAD-SNACKS', description: 'Snacks (6 items)', billedQty: 1974, dbQty: 1850, dbSource: 'uplifts.items (Catering Officer sign-off)', unitRate: 10.00, billedAmount: 19740.00, dbAmount: 18500.00, variance: 1240.00, status: 'Variance Flagged', varianceReason: 'Billed 1,974 snack units; the signed Catering Officer uplift record confirms only 1,850 received — a shortfall of 124 units.' },
        { itemCode: 'MAD-BEVERAGES', description: 'Beverages (9 items)', billedQty: 12292, dbQty: 12292, dbSource: 'uplifts.items (Catering Officer sign-off)', unitRate: 6.00, billedAmount: 73752.00, dbAmount: 73752.00, variance: 0, status: 'Matched' },
        { itemCode: 'MAD-AMENITIES', description: 'Onboard Amenities (8 items)', billedQty: 672, dbQty: 630, dbSource: 'uplifts.items (Catering Officer sign-off)', unitRate: 220.00, billedAmount: 147840.00, dbAmount: 138600.00, variance: 9240.00, status: 'Variance Flagged', varianceReason: 'Billed 672 onboard amenity units (84 pax x 8 SKUs); the signed Catering Officer uplift record confirms only 630 units received on board — a shortfall of 42 units.' }
      ]
    }
  }
];

const byFileName = SCENARIOS.reduce((acc, s) => {
  acc[s.fileName] = s;
  return acc;
}, {});

/** Looks up a demo scenario by the uploaded File's name (case-insensitive). */
export function findDemoScenario(fileName) {
  if (!fileName) return null;
  return byFileName[String(fileName).trim().toLowerCase()] || null;
}

export function listDemoFileNames() {
  return SCENARIOS.map(s => s.fileName);
}

/** Static stand-in for the real "enumerate caterers/vendors" endpoints while IS_DEMO_MODE is on. */
export const DEMO_VENDORS = [
  { id: 'v-cafs-hyd', code: 'CAFS', name: 'Casino Air Caterers & Flight Services (CAFS)', name2: 'CAFS' },
  { id: 'v-tajsats-bom', code: 'TAJSATS', name: 'TajSATS Air Catering Ltd', name2: 'TajSATS' },
  { id: 'v-ofs-del', code: 'OFS', name: 'Oberoi Flight Services', name2: 'Oberoi Flight Services' },
  { id: 'v-asc-maa', code: 'ASC', name: 'Ambassador Sky Chef', name2: 'Ambassador Sky Chef' },
  { id: 'v-atlas-cmn', code: 'ASF', name: 'Atlas Skyfood Catering SARL', name2: 'Atlas Skyfood Catering SARL' }
];

/** Static stand-in for the real "required fields for invoice type" endpoint while IS_DEMO_MODE is on. */
export const DEMO_REQUIRED_FIELDS = {
  Service: ['invoiceNumber', 'locationCode', 'startDate', 'endDate', 'sector'],
  Uplifts: ['invoiceNumber', 'locationCode', 'startDate', 'endDate', 'sector'],
  'Bulk Deliveries': ['invoiceNumber', 'locationCode', 'poNumber']
};

// ---------------------------------------------------------------------------
// Persistence: demo-submitted invoices otherwise live only in React state,
// so a page refresh during a demo would silently lose everything a presenter
// just submitted. Stash them in localStorage instead — private to this
// browser, survives reloads, never touches any real backend.
// ---------------------------------------------------------------------------

const DEMO_STORAGE_KEY = 'caterer-invoicing-demo-invoices';

/** Reads the persisted list of demo-submitted invoices (empty array if none / on error). */
export function loadPersistedDemoInvoices() {
  try {
    const raw = window.localStorage.getItem(DEMO_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    console.warn('[demoInvoiceData] Could not read persisted demo invoices:', err.message);
    return [];
  }
}

/**
 * Persists the given list of demo invoices. The uploaded PDF (a File/Blob
 * object, attached as `selectedPdf`) can't survive JSON serialization, so
 * it's dropped on save — after a reload, the review modal's existing
 * "no PDF available" fallback view is used for those rows instead.
 */
export function persistDemoInvoices(list) {
  try {
    const serializable = (list || []).map(({ selectedPdf, ...rest }) => rest);
    window.localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(serializable));
  } catch (err) {
    console.warn('[demoInvoiceData] Could not persist demo invoices:', err.message);
  }
}

/**
 * True if an invoice with this invoiceId already exists in this demo
 * (persisted or in the current session) in a status other than Rejected —
 * used to flag/block a repeat upload of the same sample PDF as a duplicate.
 * A Rejected invoice doesn't count: the caterer is expected to resubmit
 * after a rejection, so that invoiceId becomes uploadable again.
 * `existingDemoInvoices` is the array of already-submitted demo invoice
 * objects (each with `invoiceId` and `status`).
 */
export function isDuplicateInvoiceId(invoiceId, existingDemoInvoices) {
  if (!invoiceId) return false;
  return (existingDemoInvoices || []).some(inv => inv.invoiceId === invoiceId && inv.status !== 'Rejected');
}
